import React, { useMemo, useState } from 'react';
import { Shield, Upload, Eye, AlertTriangle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { withBackend } from '../utils/api';
import DocumentConversionService from '../services/documentConversionService';

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const statusCopy = {
  match: { label: 'Match', tone: 'bg-green-500/10 text-green-600 border-green-500/20' },
  issues_found: { label: 'Differences found', tone: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  missing_designed: { label: 'Missing in designed', tone: 'bg-red-500/10 text-red-600 border-red-500/20' },
  extra_designed: { label: 'Extra in designed', tone: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
};

export function EnsurePage() {
  const conversionService = useMemo(() => new DocumentConversionService(), []);
  const [originalFile, setOriginalFile] = useState(null);
  const [designedFile, setDesignedFile] = useState(null);
  const [originalPages, setOriginalPages] = useState([]);
  const [designedPages, setDesignedPages] = useState([]);
  const [isConverting, setIsConverting] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [strictness, setStrictness] = useState('balanced');
  const [qaType, setQaType] = useState('both'); // 'content', 'design', or 'both'

  const convertDocument = async (file, type) => {
    setIsConverting(true);
    try {
      const conversion = await conversionService.convertDocumentToImages(file);
      const pages = (conversion.images || []).map((page) => ({
        pageNumber: page.pageNumber,
        imageData: page.imageData,
        width: page.width,
        height: page.height,
      }));
      if (type === 'original') {
        setOriginalPages(pages);
      } else {
        setDesignedPages(pages);
      }
      return pages;
    } finally {
      setIsConverting(false);
    }
  };

  const handleFileChange = async (event, type) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }
    setComparisonResult(null);
    setErrorMessage('');
    if (type === 'original') {
      setOriginalFile(file);
      // Skip conversion - we'll send the PPTX file directly to backend
    } else {
      setDesignedFile(file);
      // Skip conversion - we'll send the PPTX file directly to backend
    }
  };

  const handleCompare = async () => {
    if (!originalFile || !designedFile) {
      setErrorMessage('Upload both the original and designed documents.');
      return;
    }

    try {
      setErrorMessage('');
      setIsComparing(true);

      let allDifferences = [];

      // Run content QA if needed
      if (qaType === 'content' || qaType === 'both') {
        const formData = new FormData();
        formData.append('file1', originalFile);
        formData.append('file2', designedFile);

        const response = await fetch(withBackend('/qa-content-comparison'), {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Content QA failed: ${response.status}`);
        }

        const result = await response.json();
        allDifferences = [...allDifferences, ...result.differences.map(d => ({ ...d, qaCategory: 'Content QA' }))];
      }

      // Run design QA if needed
      if (qaType === 'design' || qaType === 'both') {
        const formData = new FormData();
        formData.append('file1', originalFile);
        formData.append('file2', designedFile);
        formData.append('leniency', strictness);

        const response = await fetch(withBackend('/qa-design-comparison'), {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Design QA failed: ${response.status}`);
        }

        const result = await response.json();
        allDifferences = [...allDifferences, ...result.differences.map(d => ({ ...d, qaCategory: 'Design QA' }))];
      }

      // Transform to page-based structure for display
      const pageComparisons = {};
      allDifferences.forEach(diff => {
        const pageNum = diff.slide || 1;
        if (!pageComparisons[pageNum]) {
          pageComparisons[pageNum] = {
            pageNumber: pageNum,
            status: 'issues_found',
            issues: []
          };
        }
        pageComparisons[pageNum].issues.push({
          description: diff.description,
          severity: diff.severity || 'medium',
          category: diff.qaCategory,
          type: diff.type,
          before: diff.before,
          after: diff.after,
          details: diff.details
        });
      });

      const pageComparisonArray = Object.values(pageComparisons);
      const totalPages = Math.max(...allDifferences.map(d => d.slide || 1), 1);
      const pagesWithIssues = pageComparisonArray.length;
      const matchingPages = totalPages - pagesWithIssues;

      const result = {
        pageComparisons: pageComparisonArray,
        summary: {
          totalPages,
          pagesWithIssues,
          matchingPages,
          qualityScore: Math.round((matchingPages / totalPages) * 100),
          status: pagesWithIssues === 0 ? 'match' : 'issues_found',
          recommendations: allDifferences.length > 0 ? ['Review flagged differences', 'Verify brand guideline compliance'] : []
        }
      };

      setComparisonResult(result);
    } catch (error) {
      console.error('Comparison error:', error);
      setErrorMessage(error.message || 'Unable to compare documents. Please try again.');
    } finally {
      setIsComparing(false);
    }
  };

  const renderPageIssues = (page) => {
    if (!page.issues || page.issues.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">No issues detected for this page.</p>
      );
    }

    return (
      <div className="space-y-2">
        {page.issues.map((issue, index) => {
          const severityTone = issue.severity === 'high'
            ? 'text-red-500'
            : issue.severity === 'medium'
            ? 'text-yellow-600'
            : 'text-muted-foreground';

          const categoryColor = issue.category === 'Content QA'
            ? 'bg-blue-500/10 text-blue-600 border-blue-500/20'
            : 'bg-purple-500/10 text-purple-600 border-purple-500/20';

          return (
            <div key={index} className="rounded-lg border border-border/60 bg-card/50 p-3 text-sm">
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="font-medium text-foreground flex-1">{issue.description}</p>
                {issue.category && (
                  <Badge variant="secondary" className={`px-2 py-0.5 text-xs border ${categoryColor}`}>
                    {issue.category}
                  </Badge>
                )}
              </div>
              <p className={`text-xs ${severityTone} capitalize`}>Severity: {issue.severity}</p>
              {issue.type && (
                <p className="text-xs text-muted-foreground mt-1">Type: {issue.type.replace(/_/g, ' ')}</p>
              )}
              {issue.before && issue.after && (
                <div className="mt-3 space-y-2">
                  <div className="rounded bg-red-500/10 border border-red-500/20 p-2">
                    <p className="text-xs font-semibold text-red-600 mb-1">Original:</p>
                    <p className="text-xs text-foreground/80">{issue.before}</p>
                  </div>
                  <div className="rounded bg-green-500/10 border border-green-500/20 p-2">
                    <p className="text-xs font-semibold text-green-600 mb-1">Changed to:</p>
                    <p className="text-xs text-foreground/80">{issue.after}</p>
                  </div>
                </div>
              )}
              {issue.details && (
                <div className="mt-2 rounded bg-muted/50 p-2">
                  <p className="text-xs text-muted-foreground">
                    Details: {Array.isArray(issue.details) ? issue.details.join(', ') : issue.details}
                  </p>
                </div>
              )}
              {issue.recommendation && (
                <p className="text-xs text-muted-foreground mt-1">{issue.recommendation}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSummary = () => {
    if (!comparisonResult?.summary) {
      return null;
    }

    const { summary } = comparisonResult;
    const tone = summary.qualityScore >= 90
      ? 'text-green-600'
      : summary.qualityScore >= 75
      ? 'text-yellow-600'
      : 'text-red-600';

    return (
      <Card className="bg-gradient-to-br from-accent/5 to-accent/10 border-accent/20">
        <CardHeader>
          <CardTitle>QA Overview</CardTitle>
          <CardDescription>
            Assessment based on slide-by-slide structure, content alignment, and visual consistency.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center">
            <p className={`text-4xl font-bold ${tone}`}>{summary.qualityScore}%</p>
            <p className="text-sm text-muted-foreground">Quality score</p>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-semibold text-foreground">Total pages</p>
              <p className="text-muted-foreground">{summary.totalPages}</p>
            </div>
            <div>
              <p className="font-semibold text-foreground">Pages needing review</p>
              <p className="text-muted-foreground">{summary.pagesWithIssues}</p>
            </div>
            <div>
              <p className="font-semibold text-foreground">Matching pages</p>
              <p className="text-muted-foreground">{summary.matchingPages}</p>
            </div>
            <div>
              <p className="font-semibold text-foreground">Status</p>
              <p className="text-muted-foreground capitalize">{summary.status.replace('_', ' ')}</p>
            </div>
          </div>
          {summary.recommendations && summary.recommendations.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Recommendations</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {summary.recommendations.map((item, index) => (
                  <li key={index}>� {item}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-8 space-y-8 min-h-screen max-w-7xl mx-auto">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-foreground">Ensure</h1>
        <p className="text-lg text-muted-foreground">
          QA check original vs. designed deliverables before sending to clients.
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-[360px_minmax(0,1fr)]">
        <Card className="bg-card/60 backdrop-blur-sm border-border/50 space-y-6 p-6">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-foreground">Upload source files</h2>
            <p className="text-sm text-muted-foreground">
              Supported: PDF, PPT, PPTX. We convert slides locally to images and analyse them on the server.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Original document</label>
              <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border/60 bg-background/40 p-6 text-center cursor-pointer hover:border-accent/60">
                <Upload className="h-6 w-6 text-accent" />
                <span className="text-sm text-muted-foreground">Drop or click to upload</span>
                <input type="file" accept=".pdf,.ppt,.pptx" className="hidden" onChange={(event) => handleFileChange(event, 'original')} />
              </label>
              {originalFile && (
                <div className="rounded-lg border border-border/60 bg-card/50 p-3 text-sm">
                  <p className="text-foreground font-medium">{originalFile.name}</p>
                  <p className="text-muted-foreground text-xs">{formatBytes(originalFile.size)}</p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Designed document</label>
              <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border/60 bg-background/40 p-6 text-center cursor-pointer hover:border-accent/60">
                <Eye className="h-6 w-6 text-accent" />
                <span className="text-sm text-muted-foreground">Drop or click to upload</span>
                <input type="file" accept=".pdf,.ppt,.pptx" className="hidden" onChange={(event) => handleFileChange(event, 'designed')} />
              </label>
              {designedFile && (
                <div className="rounded-lg border border-border/60 bg-card/50 p-3 text-sm">
                  <p className="text-foreground font-medium">{designedFile.name}</p>
                  <p className="text-muted-foreground text-xs">{formatBytes(designedFile.size)}</p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-muted-foreground">QA Type</label>
            <Select value={qaType} onValueChange={setQaType}>
              <SelectTrigger className="bg-input border-border">
                <SelectValue placeholder="Select QA type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Both (Content + Design)</SelectItem>
                <SelectItem value="content">Content QA Only</SelectItem>
                <SelectItem value="design">Design QA Only</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {qaType === 'both' && 'Checks both text content and brand guideline compliance'}
              {qaType === 'content' && 'Ensures text content is identical between versions'}
              {qaType === 'design' && 'Checks brand guideline compliance (colors, fonts, layout)'}
            </p>
          </div>

          {(qaType === 'design' || qaType === 'both') && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-muted-foreground">Strictness (Design QA)</label>
              <Select value={strictness} onValueChange={setStrictness}>
                <SelectTrigger className="bg-input border-border">
                  <SelectValue placeholder="Select strictness" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="balanced">Balanced</SelectItem>
                  <SelectItem value="lenient">Lenient</SelectItem>
                  <SelectItem value="strict">Strict</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Strict mode highlights even the smallest visual differences. Balanced focuses on meaningful changes.
              </p>
            </div>
          )}

          {errorMessage && (
            <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
              <AlertTriangle className="mt-0.5 h-4 w-4" />
              <span>{errorMessage}</span>
            </div>
          )}

          <Button
            onClick={handleCompare}
            disabled={isComparing || isConverting || !originalFile || !designedFile}
            className="w-full bg-gradient-to-r from-accent to-accent/90 text-accent-foreground h-12"
          >
            {(isComparing || isConverting) ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {isConverting ? 'Preparing documents�' : 'Comparing documents�'}
              </>
            ) : (
              <>
                <Shield className="w-5 h-5 mr-2" />
                Run QA Comparison
              </>
            )}
          </Button>
        </Card>

        <div className="space-y-6">
          {renderSummary()}

          {comparisonResult && comparisonResult.pageComparisons ? (
            <div className="space-y-4">
              {comparisonResult.pageComparisons.map((page) => {
                const tone = statusCopy[page.status] || statusCopy.match;
                return (
                  <Card key={page.pageNumber} className="bg-card/60 border-border/50">
                    <CardHeader className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base text-foreground">Page {page.pageNumber}</CardTitle>
                        {page.differenceRatio != null && (
                          <CardDescription>
                            Difference ratio: {(page.differenceRatio * 100).toFixed(1)}%
                          </CardDescription>
                        )}
                      </div>
                      <Badge variant="secondary" className={`px-3 py-1 text-xs border ${tone.tone}`}>
                        {tone.label}
                      </Badge>
                    </CardHeader>
                    <CardContent>
                      {renderPageIssues(page)}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="bg-card/50 border-border/50">
              <CardContent className="py-16 text-center text-muted-foreground space-y-3">
                <Shield className="mx-auto h-10 w-10 text-accent" />
                <p className="text-sm">Upload both versions and run the comparison to see a slide-by-slide QA report.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
